import { join, map } from "lodash";
import { Styles } from "polished/lib/types/style";
import { css } from "styled-components";
import { Color, transparentize } from "@glossy";

/**
 * CSS to show disabled state of an input
 *
 * @example
 * const div = styled.input`
 *   ${inputDisabled()}
 * `
 * }
 */
const inputDisabled = (): Styles => ({
  opacity: 0.3,
  pointerEvents: "none",
});

/**
 * CSS to display an elegant thin scrollbar.
 *
 * @example
 * const div = styled.div`
 *   ${prettyScrollbar()}
 * `
 */
const prettyScrollbars = () => css`
  overflow: auto;
  &::-webkit-scrollbar {
    width: 4px;
    background-color: transparent;
    &-thumb {
      background-color: ${transparentize(0.75, Color.Black)};
      border-radius: 2px;
    }
    &-track {
      background-color: transparent;
    }
  }
`;

/**
 * CSS to hide scrollbar.
 *
 * @example
 * const div = styled.div`
 *   ${hideScrollbar()}
 * `
 */
const hideScrollbar = () => css`
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

interface CBProps {
  color1?: string;
  color2?: string;
  opacity?: number;
  size?: number;
}

/**
 * CSS to create a customized checkerboard background.
 *
 * @example
 * const div = styled.div`
 *   ${checkerboard({color1: "pink", color2: "purple", opacity: .5, size: 2})}
 * `
 */
const checkerboard = ({
  color1 = "#000",
  color2 = "#FFF",
  opacity = 1,
  size = 1,
}: CBProps): Styles => ({
  backgroundImage:
    'url("data:image/svg+xml;base64,' +
    window.btoa(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2 2" width="2" opacity="${opacity}">
        <g fill="${color1}">
          <rect width="1" height="1"/>
          <rect x="1" y="1" width="1" height="1"/>
        </g>
        <g fill="${color2}">
          <rect x="1" width="1" height="1"/>
          <rect y="1" width="1" height="1"/>
        </g>
      </svg>`
    ) +
    '")',
  backgroundSize: `${size * 2}px ${size * 2}px`,
  backgroundPosition: `0 0, ${size}px ${size}px`,
});

/**
 * Superimpose several background colors
 * @param colors
 */
const backgroundColors = (colors: string[]): Styles => ({
  backgroundImage: join(
    map(colors, (color) => `linear-gradient(${color}, ${color})`)
  ),
});

interface CFProps {
  red: number;
  green: number;
  blue: number;
  alpha?: number;
}

/**
 * CSS to create a svg filter to apply color filter on image.
 *
 * @example
 * const div = styled.div`
 *   ${colorFilter({red: 0.157, green: 0.733, blue: 0.733, alpha: 1})}
 * `
 */

const colorFilter = ({ red, green, blue, alpha = 1 }: CFProps): Styles => ({
  filter: `url("data:image/svg+xml,\
    <svg xmlns='http://www.w3.org/2000/svg'>\
      <filter id='colorFilter'>\
      <feColorMatrix type='matrix' values='${red / 255} 0 0 0 0 0 ${
    green / 255
  } 0 0 0 0 0 ${blue / 255} 0 0 0 0 0 ${alpha} 0' />\
      </filter>\
    </svg>#colorFilter")`,
});

const AI_GRADIENT = { start: "#B627A1", end: "#1D9F9F" };

/**
 * CSS to create a border gradient for AI cards.
 *
 * @example
 * const div = styled.div`
 *   ${aiBorderGradient("white")}
 * `
 */
const aiBorderGradient = (background: string): Styles => ({
  border: "1px solid transparent",
  background: `linear-gradient(to top right, ${background}, ${background}) padding-box,
               linear-gradient(to right, ${AI_GRADIENT.start}, ${AI_GRADIENT.end}) border-box`,
  borderRadius: "8px", // Vous pouvez ajuster cette valeur selon vos besoins
});

/**
 * CSS to create a text gradient for AI card titles.
 *
 * @example
 * const title = styled.h1`
 *   ${aiTextGradient()}
 * `
 */
const aiTextGradient = (): Styles => ({
  background: `linear-gradient(to right, ${AI_GRADIENT.start}, ${AI_GRADIENT.end})`,
  "-webkit-background-clip": "text",
  "-webkit-text-fill-color": "transparent",
});

export {
  backgroundColors,
  checkerboard,
  inputDisabled,
  prettyScrollbars,
  hideScrollbar,
  colorFilter,
  aiBorderGradient,
  aiTextGradient,
  AI_GRADIENT,
};
